import {InteractionRequiredAuthError, InteractionType} from '@azure/msal-browser';
import {useIsAuthenticated, useMsal, useMsalAuthentication} from '@azure/msal-react';
import {useEffect} from 'react';

import {useLogoutSync} from '../../hooks/useLogoutSync';
import {MainErrorScreen} from '../Error/MainErrorScreen';

import type {RedirectRequest, SilentRequest} from '@azure/msal-browser';

type AuthGateProps = {
  children: React.ReactNode;
  mainRequest: RedirectRequest | SilentRequest;
  fallback?: React.ReactNode;
  filterByActiveAccount?: boolean;
};

export function AuthGate({
  children,
  mainRequest,
  fallback = null,
  filterByActiveAccount = false,
}: AuthGateProps): React.JSX.Element {
  const {instance} = useMsal();
  const authorityEnv = instance.getConfiguration().auth.knownAuthorities?.[0];
  const active = instance.getActiveAccount();
  const first =
    instance.getAllAccounts().find((a) => !authorityEnv || a.environment === authorityEnv) ??
    null;
  const isAuthenticated = useIsAuthenticated(
    filterByActiveAccount && active ? {username: active.username} : undefined
  );

  useLogoutSync();

  // needed for Silent flow; clear stale active accounts from a different authority (e.g. viewer vs main tenant)
  useEffect(() => {
    if (active && authorityEnv && active.environment !== authorityEnv) {
      instance.setActiveAccount(null);
    } else if (!active && first) {
      instance.setActiveAccount(first);
    }
  }, [active, authorityEnv, first, instance]);

  // MsalAuthenticatedTemplate doesn't allow fallback for Silent fallback
  const {login, error} = useMsalAuthentication(InteractionType.Silent, mainRequest);

  const shouldRetryAfterError =
    !!error &&
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md#ssosilent-example
    (error instanceof InteractionRequiredAuthError ||
      // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/errors.md#x-frame-options-deny
      error.errorCode === 'monitor_window_timeout' ||
      error.errorCode === 'timed_out' ||
      // https://learn.microsoft.com/en-us/azure/active-directory-b2c/troubleshoot?pivots=b2c-user-flow#user-canceled-the-operation
      error.errorCode === 'access_denied' ||
      // cached account from a different authority (e.g. regular tenant vs viewer tenant) — clear via useEffect then redirect
      error.errorCode === 'authority_mismatch');

  useEffect(() => {
    if (shouldRetryAfterError) {
      void login(InteractionType.Redirect, {...mainRequest, redirectUri: undefined});
    }
  }, [shouldRetryAfterError, login, mainRequest, error]);

  if (error && !shouldRetryAfterError) {
    return (
      <MainErrorScreen error={error} handleRetry={() => window.location.reload()} />
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return isAuthenticated ? <>{children}</> : <>{fallback}</>;
}
